import { VariantProps, cva } from "class-variance-authority";
import React from "react";
import { FlexProps } from "../../primitives";
import { IconName } from "../../primitives/Icon/Icon.types";

export const disabledCssClasses =
  "disabled:cursor-not-allowed disabled:opacity-60 disabled:text-neutral-400 disabled:bg-secondary";

export const disabledWrapperClasses =
  "cursor-not-allowed !bg-secondary text-neutral-400";

export const disabledOutlineClasses =
  "focus-visible:outline-none focus:outline-none focus-visible:ring-inset";

export const formControlWrapperVariants = cva(
  "relative flex flex-row items-center w-full",
  {
    variants: {
      variant: {
        default:
          "bg-primary-background dark:bg-transparent px-2 rounded-md border border-input transition-colors focus:outline-none focus-visible:ring-inset focus-visible:outline-amber-500",
        unstyled:
          "rounded-none border-none bg-transparent shadow-none focus-visible:ring-inset",
        outlined: ""
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);

export interface FormControlWrapperOptions {
  /** Id of the input, used by the label and messages. Generated when omitted. */
  id?: string;
  /** Disables the field and greys it out. */
  isDisabled?: boolean;
  /** Shows the value without allowing edits (grey background, readable text). */
  isReadOnly?: boolean;
  /** Forces the invalid look. By default the field is invalid whenever `error` is set. */
  isInvalid?: boolean;
  /** Adds a red asterisk to the label and marks the input `aria-required`. */
  isRequired?: boolean;
  /** Adds an "Optional" tag on the right of the label. */
  isOptional?: boolean;
  isLoading?: boolean;
  placeholder?: string;
  /** Validation error shown under the field (as an error alert) and announced with it. */
  error?: React.ReactNode;
  /** Confirmation message shown under the field, in green. */
  success?: React.ReactNode;
  /** Hint under the field, announced as the input's description. */
  helperText?: React.ReactNode;
  /** Label above the field, linked to the input. */
  label?: React.ReactNode;
  /** Longer explanation between the label and the field. */
  description?: React.ReactNode;
  /** Content of an info tooltip shown at the right of the label. */
  info?: React.ReactNode;
  /** Elements shown inside the field, before the input (e.g. a prefix). */
  leftSlot?: React.ReactNode[];
  /** Elements shown inside the field, after the input (e.g. a unit or an action). */
  rightSlot?: React.ReactNode[];
  /** Maximum length; also shown by `showCounter`. */
  maxLength?: number;
  /** Minimum length; shown by `showCounter` until it is reached. */
  minLength?: number;
  /** Shows "N of M characters" under the field. */
  showCounter?: boolean;
}

export interface FormControlWrapperState extends FormControlWrapperOptions {
  charactersCounter: number;
  setCharactersCounter: (value: number) => void;
}

export interface FormControlWrapperProps
  extends React.PropsWithChildren,
    FormControlWrapperOptions,
    VariantProps<typeof formControlWrapperVariants> {
  /** Classes for the outer column (label + field + messages). */
  className?: string;
  /** Icon inside the field on the left: a Lucide icon name (`"Mail"`) or a node. */
  leftIcon?: IconName | React.ReactNode;
  /** Icon inside the field on the right: a Lucide icon name or a node. */
  rightIcon?: IconName | React.ReactNode;
  /** Clicking an icon focuses the input. */
  enableSlotsInputFocus?: boolean;
  /** Clicking anywhere on the field focuses the input. */
  enableWrapperInputFocus?: boolean;
  /** Classes for the bordered field box. */
  wrapperClassName?: string;
  /**
   * Reserves no space for the error when there is none. Set to `false` to keep the layout
   * stable when an error appears.
   * @default true
   */
  hideEmptyMessages?: boolean;
  wrapperRef?: React.LegacyRef<HTMLInputElement>;
  /**
   * `default` (bordered box), `unstyled` (no border or background, for fields embedded in
   * another control) or `outlined`.
   * @default "default"
   */
  variant?: VariantProps<typeof formControlWrapperVariants>["variant"];
}

export interface FormControlLabelProps
  extends React.PropsWithChildren<React.LabelHTMLAttributes<HTMLLabelElement>> {
  isRequired?: boolean;
  isOptional?: boolean;
  htmlFor?: string;
  info?: React.ReactNode;
}

export interface FormControlSlotProps
  extends React.PropsWithChildren,
    FlexProps {}

export const FormControlWrapperForwardedProps = [
  "isDisabled",
  "isReadOnly",
  "isInvalid",
  "isRequired",
  "isOptional",
  "error",
  "success",
  "label",
  "id",
  "leftIcon",
  "rightIcon",
  "helperText",
  "leftSlot",
  "rightSlot",
  "className",
  "wrapperClassName",
  "minLength",
  "maxLength",
  "showCounter",
  "description",
  "info",
  "hideEmptyMessages",
  "wrapperRef"
] as (keyof FormControlWrapperProps)[];

/** Id of the helper text of the field whose input id is `inputId`, for aria-describedby. */
export const getHelperTextId = (inputId?: string) => `${inputId}-helper`;

/** Id of the error message of the field whose input id is `inputId`, for aria-describedby. */
export const getErrorId = (inputId?: string) => `${inputId}-error`;
